import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Typography,
  Chip
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import authApi from '../../services/authApi';
import { useUserSession } from '../../contexts/UserContext';
import DataTable from '../common/DataTable';

const ScheduleManager = ({ selectedLists, onClose, onSelectedListsChange, lists = [] }) => {
  const [schedules, setSchedules] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentSchedule, setCurrentSchedule] = useState({
    name: '',
    frequency: 'Daily',
    day_of_week: 1,
    day_of_month: 1,
    time_of_day: new Date(),
    report_settings: {},
    is_active: true
  });
  const { userSession } = useUserSession();

  const fetchSchedules = useCallback(async () => {
    try {
      setLoading(true);
      const token = authApi.getToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/schedules`, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      const data = await response.json();
      setSchedules(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  }, [userSession?.email]);

  useEffect(() => {
    if (userSession?.email) {
      fetchSchedules();
    }
  }, [userSession?.email, fetchSchedules]);

  const handleOpenDialog = (schedule = null) => {
    if (schedule) {
      let parsedSettings = {};
      try {
        parsedSettings = JSON.parse(schedule.report_settings);
      } catch (error) {
        console.error('Error parsing report settings:', error);
      }

      setCurrentSchedule({
        ...schedule,
        time_of_day: new Date(schedule.time_of_day),
        report_settings: parsedSettings
      });
    } else {
      setCurrentSchedule({
        name: '',
        frequency: 'Daily',
        day_of_week: 1,
        day_of_month: 1,
        time_of_day: new Date(),
        report_settings: {
          selectedLists: selectedLists
        },
        is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      const token = authApi.getToken();
      const method = currentSchedule.id ? 'PUT' : 'POST';
      const url = currentSchedule.id 
        ? `${process.env.REACT_APP_API_URL}/api/schedules/${currentSchedule.id}`
        : `${process.env.REACT_APP_API_URL}/api/schedules`;

      const timeString = typeof currentSchedule.time_of_day === 'string' 
        ? currentSchedule.time_of_day 
        : currentSchedule.time_of_day instanceof Date 
          ? `${String(currentSchedule.time_of_day.getHours()).padStart(2, '0')}:${String(currentSchedule.time_of_day.getMinutes()).padStart(2, '0')}`
          : '00:00';

      const scheduleData = {
        ...currentSchedule,
        report_type: 'SecurityAudit',
        report_settings: JSON.stringify({
          selectedLists: selectedLists
        })
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: scheduleData.name,
          frequency: scheduleData.frequency,
          day_of_week: scheduleData.frequency === 'Weekly' ? scheduleData.day_of_week : null,
          day_of_month: scheduleData.frequency === 'Monthly' ? scheduleData.day_of_month : null,
          time_of_day: timeString,
          report_settings: scheduleData.report_settings,
          is_active: true,
          user_email: userSession.email
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save schedule');
      }
      
      setOpenDialog(false);
      fetchSchedules();
    } catch (error) {
      console.error('Error saving schedule:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = authApi.getToken();
      await fetch(`${process.env.REACT_APP_API_URL}/api/schedules/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      fetchSchedules();
    } catch (error) {
      console.error('Error deleting schedule:', error);
    }
  };

  const columns = [
    { id: 'name', label: 'Name' },
    { id: 'frequency', label: 'Frequency' },
    { 
      id: 'time_of_day', 
      label: 'Time',
      render: (row) => new Date(row.time_of_day).toLocaleTimeString()
    },
    {
      id: 'next_run',
      label: 'Next Run',
      render: (row) => row.next_run ? new Date(row.next_run).toLocaleString() : 'N/A'
    },
    {
      id: 'is_active',
      label: 'Status',
      render: (row) => row.is_active ? 'Active' : 'Inactive'
    },
    {
      id: 'actions',
      label: 'Actions',
      render: (row) => (
        <Box>
          <IconButton onClick={() => handleOpenDialog(row)}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => handleDelete(row.id)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Report Schedules</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Schedule
        </Button>
      </Box>

      <DataTable
        columns={columns}
        data={schedules}
        loading={loading}
      />

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {currentSchedule.id ? 'Edit Schedule' : 'New Schedule'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Schedule Name"
                value={currentSchedule.name}
                onChange={(e) => setCurrentSchedule({ ...currentSchedule, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={currentSchedule.frequency}
                  onChange={(e) => setCurrentSchedule({ ...currentSchedule, frequency: e.target.value })}
                >
                  <MenuItem value="Daily">Daily</MenuItem>
                  <MenuItem value="Weekly">Weekly</MenuItem>
                  <MenuItem value="Monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {currentSchedule.frequency === 'Weekly' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Day of Week</InputLabel>
                  <Select
                    value={currentSchedule.day_of_week}
                    onChange={(e) => setCurrentSchedule({ ...currentSchedule, day_of_week: e.target.value })}
                  >
                    <MenuItem value={0}>Sunday</MenuItem>
                    <MenuItem value={1}>Monday</MenuItem>
                    <MenuItem value={2}>Tuesday</MenuItem>
                    <MenuItem value={3}>Wednesday</MenuItem>
                    <MenuItem value={4}>Thursday</MenuItem>
                    <MenuItem value={5}>Friday</MenuItem>
                    <MenuItem value={6}>Saturday</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            {currentSchedule.frequency === 'Monthly' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Day of Month"
                  value={currentSchedule.day_of_month}
                  onChange={(e) => setCurrentSchedule({ 
                    ...currentSchedule, 
                    day_of_month: Math.min(31, Math.max(1, parseInt(e.target.value) || 1))
                  })}
                  InputProps={{ inputProps: { min: 1, max: 31 } }}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Time (24-hour format)"
                type="time"
                value={currentSchedule.time_of_day instanceof Date 
                  ? currentSchedule.time_of_day.toLocaleTimeString('en-US', {
                      hour12: false,
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : currentSchedule.time_of_day}
                onChange={(e) => {
                  const [hours, minutes] = e.target.value.split(':');
                  const newDate = new Date();
                  newDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                  setCurrentSchedule({ ...currentSchedule, time_of_day: newDate });
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  step: 300, // 5 min
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Selected Lists for Report
              </Typography>
              {selectedLists?.length > 0 ? (
                <Box sx={{ mt: 1 }}>
                  {lists
                    .filter(list => selectedLists.includes(list.id))
                    .map(list => (
                      <Chip
                        key={list.id}
                        label={list.name || list.title}
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No lists selected
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScheduleManager; 